// apps/web/src/lib/reminderPrefs.ts
// The reminder settings, fetched once per session.
//
// The scheduler re-runs on every foreground and after every write, and none of
// those moments need a fresh copy of preferences the user only changes from one
// screen. That screen invalidates.
import { api } from "./api";
import { todayLocal } from "./date";
import { prefsFromApi, syncReminders, cancelAllReminders, type ReminderTexts } from "./reminderScheduler";
import type { ReminderPrefs } from "@shared/notifications";

let inflight: Promise<ReminderPrefs | null> | null = null;

export function getReminderPrefs(): Promise<ReminderPrefs | null> {
  inflight ??= api.getReminders()
    .then(prefsFromApi)
    // Null, not defaults: a failed fetch must not be mistaken for "the user has
    // nothing switched on", because the scheduler would then cancel reminders
    // they do have. Skipping a sync is the safe failure.
    .catch(() => null);
  return inflight;
}

export function invalidateReminderPrefs(): void {
  inflight = null;
}

/**
 * Re-read the settings and rebuild the schedule from the server's picture of
 * today. Today rebuilds from the payload it already holds; this is for the
 * settings screen, which has no day loaded and must not leave a just-changed
 * toggle unapplied until the user happens to visit Today.
 */
export async function resyncReminders(texts: ReminderTexts, lang: "en" | "de"): Promise<void> {
  invalidateReminderPrefs();
  const prefs = await getReminderPrefs();
  if (!prefs) return;
  if (!prefs.master) { await cancelAllReminders(); return; }
  const today = await api.today(todayLocal(), lang).catch(() => null);
  if (!today) return;
  await syncReminders(today, prefs, texts);
}
